import { useState, useEffect, useCallback } from 'react';
import { Users, MagnifyingGlass, ShieldCheck, Envelope, Phone, LinkedinLogo, Trash } from '@phosphor-icons/react';
import EmailAuditRescuePanel from '../components/EmailAuditRescuePanel';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_CATEGORIES = ['techniek', 'onderhoud', 'management', 'directie', 'inkoop', 'exploitatie'];

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [roleCategory, setRoleCategory] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showEmailAudit, setShowEmailAudit] = useState(false);

  const [contactDims, setContactDims] = useState([]);
  const [dimValues, setDimValues] = useState({});

  useEffect(() => {
    fetch(`${API}/profiles/filter-config`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setContactDims(d.contact_dimensions || []); setDimValues({}); })
      .catch(() => {});
  }, []);

  const fetchContacts = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (country) params.set('country', country);
    if (roleCategory) params.set('role_category', roleCategory);
    if (verifiedOnly) params.set('verified_only', 'true');
    Object.entries(dimValues).forEach(([k, v]) => {
      if (v) params.set(`dim_${k}`, v);
    });
    params.set('limit', '100');
    setLoading(true);
    fetch(`${API}/contacts?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setContacts(data.contacts || []); setTotal(data.total || 0); })
      .finally(() => setLoading(false));
  }, [search, country, roleCategory, verifiedOnly, dimValues]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    if (selected.size === contacts.length && contacts.length > 0) setSelected(new Set());
    else setSelected(new Set(contacts.map(c => c.contact_id)));
  };

  const deleteOne = async (id, name) => {
    if (!window.confirm(`Contact "${name}" verwijderen?`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/contacts/${id}`, { method: 'DELETE', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verwijderen mislukt');
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      fetchContacts();
    } catch (e) { window.alert('Fout: ' + e.message); }
    finally { setDeleting(false); }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${ids.length} geselecteerde contacten verwijderen?`)) return;
    setDeleting(true);
    try {
      const r = await fetch(`${API}/contacts/bulk-delete`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contact_ids: ids, confirm: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Verwijderen mislukt');
      window.alert(`✓ ${d.contacts_deleted} contacten verwijderd.`);
      setSelected(new Set());
      fetchContacts();
    } catch (e) { window.alert('Fout: ' + e.message); }
    finally { setDeleting(false); }
  };

  return (
    <div className="animate-fade-in" data-testid="contacts-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Contacts</h1>
          <p className="text-sm text-zinc-500 mt-1">
            <span className="font-data font-bold">{total}</span> contact persons found
            {selected.size > 0 && <span className="ml-2 text-zinc-700">· {selected.size} geselecteerd</span>}
          </p>
        </div>
        {selected.size > 0 && (
          <button
            data-testid="bulk-delete-contacts-btn"
            onClick={deleteSelected}
            disabled={deleting}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            <Trash size={14} weight="bold" /> Verwijder {selected.size}
          </button>
        )}
        <button
          data-testid="purge-blocked-emails-btn"
          onClick={async () => {
            if (!window.confirm('Alle contacten met emails op de blocklist (duckduckgo.com etc.) opschonen voor dit profiel?\n\nKlik OK om de emails te NULLEN. Klik Cancel om af te breken.')) return;
            const delAll = window.confirm('Wil je deze contacten VOLLEDIG verwijderen (i.p.v. alleen email blanken)?\n\nOK = verwijder · Cancel = email leegmaken');
            setDeleting(true);
            try {
              const r = await fetch(`${API}/contacts/purge-blocked-emails`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirm: true, delete_contacts: delAll }),
              });
              const d = await r.json();
              if (!r.ok) throw new Error(d.detail || 'Schoonmaak mislukt');
              window.alert(`✓ Profiel "${d.profile}" opgeschoond:\n· Contacten verwijderd: ${d.contacts_deleted}\n· Contacten email leeggemaakt: ${d.contacts_email_blanked}\n· Facilities opgeschoond: ${d.facilities_cleaned}`);
              fetchContacts();
            } catch (e) { window.alert('Fout: ' + e.message); }
            finally { setDeleting(false); }
          }}
          disabled={deleting}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-amber-300 text-amber-800 rounded-sm hover:bg-amber-50 disabled:opacity-50 transition-colors"
          title="Verwijder contacten met emails op blocklist (duckduckgo.com etc.)"
        >
          <Trash size={14} /> Blocked emails opschonen
        </button>

        <button
          data-testid="email-audit-rescue-btn"
          onClick={() => setShowEmailAudit(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-white border border-emerald-300 text-emerald-800 rounded-sm hover:bg-emerald-50 transition-colors"
          title="Audit + automatisch missende emails ophalen via facility websites"
        >
          <Envelope size={14} /> Email Audit & Rescue
        </button>
      </div>

      {/* Filters */}
      <div className="border border-zinc-200 bg-white p-4 mb-6" data-testid="contacts-filters">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="relative col-span-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input data-testid="contact-search-input" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 rounded-sm text-sm" />
          </div>
          <Select value={country} onValueChange={v => setCountry(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="contact-country-filter" className="rounded-sm text-sm"><SelectValue placeholder="Country" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Countries</SelectItem>
              <SelectItem value="NL">Netherlands</SelectItem>
              <SelectItem value="BE">Belgium</SelectItem>
              <SelectItem value="FR">France</SelectItem>
              <SelectItem value="DE">Germany</SelectItem>
            </SelectContent>
          </Select>
          <Select value={roleCategory} onValueChange={v => setRoleCategory(v === 'all' ? '' : v)}>
            <SelectTrigger data-testid="contact-role-filter" className="rounded-sm text-sm"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {ROLE_CATEGORIES.map(r => <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Dynamic contact dimensions per profile */}
          {contactDims.map(dim => (
            <Select
              key={dim.key}
              value={dimValues[dim.key] || ''}
              onValueChange={v => setDimValues(prev => ({ ...prev, [dim.key]: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid={`contact-dim-${dim.key}`} className="rounded-sm text-sm">
                <SelectValue placeholder={dim.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{dim.label} — Alle</SelectItem>
                {(dim.options || []).map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={verifiedOnly} onChange={e => setVerifiedOnly(e.target.checked)} className="rounded-sm" />
            Verified only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 overflow-hidden" data-testid="contacts-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-3 py-2.5 w-8">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === contacts.length}
                  onChange={toggleSelectAll}
                  className="rounded-sm cursor-pointer"
                  data-testid="select-all-contacts"
                />
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Contact</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Role</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Organization</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Email</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Confidence</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Level</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-zinc-500">Links</th>
              <th className="px-3 py-2.5 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-400">Loading...</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-400">No contacts found</td></tr>
            ) : (
              contacts.map(c => (
                <tr key={c.contact_id} data-testid={`contact-row-${c.contact_id}`} className={`border-b border-zinc-100 hover:bg-zinc-50 ${selected.has(c.contact_id) ? 'bg-blue-50/40' : ''}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(c.contact_id)}
                      onChange={() => toggleSelect(c.contact_id)}
                      className="rounded-sm cursor-pointer"
                      data-testid={`select-contact-${c.contact_id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{c.full_name}</div>
                    <Badge className="rounded-full px-2 py-0 text-[10px] font-data bg-zinc-100 text-zinc-600 mt-0.5">{c.country_code}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{c.role_title}</div>
                    <Badge className="rounded-full px-2 py-0 text-[10px] font-data bg-zinc-100 text-zinc-600 mt-0.5">{c.role_category}</Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">{c.organization}</td>
                  <td className="px-4 py-3">
                    {c.email ? (
                      <div className="flex items-center gap-1.5">
                        <Envelope size={14} className="text-zinc-400" />
                        <span className="font-data text-xs">{c.email}</span>
                        <Badge className={`rounded-full px-1.5 py-0 text-[10px] font-data ${c.email_status === 'verified' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                          {c.email_status}
                        </Badge>
                      </div>
                    ) : <span className="text-zinc-400 text-xs">-</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-data text-sm font-bold ${c.confidence_score >= 0.8 ? 'text-emerald-600' : c.confidence_score >= 0.6 ? 'text-amber-600' : 'text-red-500'}`}>
                      {(c.confidence_score * 100).toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{c.decision_level}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {c.phone && <Phone size={16} className="text-zinc-400 hover:text-zinc-700 cursor-pointer" />}
                      {c.linkedin_url && (
                        <a href={c.linkedin_url} target="_blank" rel="noreferrer">
                          <LinkedinLogo size={16} className="text-zinc-400 hover:text-[#0055FF]" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <button
                      data-testid={`delete-contact-${c.contact_id}`}
                      onClick={() => deleteOne(c.contact_id, c.full_name)}
                      disabled={deleting}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-sm transition-colors disabled:opacity-40"
                      title="Verwijder"
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <EmailAuditRescuePanel
        open={showEmailAudit}
        onClose={() => setShowEmailAudit(false)}
        onRefreshContacts={fetchContacts}
      />
    </div>
  );
}
